import { v4 as uuidv4 } from 'uuid';
import isNil from 'lodash/isNil';
import surveyUrls from '../surveyUrls';
import { SurveyBuilderQuestionTypes } from '../SurveyBuilderQuestionTypes';
import SurveyItem, { SurveyItemTypographyVariants } from '../SurveyItem';
import { AutopopulateSourceResourceType } from '../SurveyItemAutopopulation';

export type EnablementComparators =
  | '='
  | '!='
  | 'hasAnswer'
  | 'doesNotHaveAnswer'
  | '<'
  | '<='
  | '>'
  | '>=';

export type SupportedEnablementTypes =
  | 'answerBoolean'
  | 'answerDecimal'
  | 'answerInteger'
  | 'answerString'
  | 'answerCoding'
  | 'answerQuantity'
  | 'answerTime'
  | 'answerDate'
  | 'answerDateTime'
  | 'answerAttachment';

const customEnablementComparators: EnablementComparators[] = [
  '!=',
  '<',
  '<=',
  '>',
  '>=',
];

export class EnablementBuilder {
  private _linkId: string;
  private _enablementDependentType: SupportedEnablementTypes;
  private _comparator: EnablementComparators;
  private _targetValue: any;

  constructor(
    linkId: string,
    comparator: EnablementComparators,
    enablementDependentType: SupportedEnablementTypes,
    targetValue: any,
  ) {
    this._linkId = linkId;
    this._enablementDependentType = enablementDependentType;
    this._comparator = comparator;
    this._targetValue = targetValue;
  }

  private _answerEnablementValue = (
    targetType: SupportedEnablementTypes,
    value: any,
  ): Partial<fhir.QuestionnaireItemEnableWhen> => {
    // runtime target type check
    if (
      targetType !== 'answerBoolean' &&
      targetType !== 'answerDecimal' &&
      targetType !== 'answerInteger' &&
      targetType !== 'answerString' &&
      targetType !== 'answerCoding' &&
      targetType !== 'answerQuantity' &&
      targetType !== 'answerTime' &&
      targetType !== 'answerDate' &&
      targetType !== 'answerDateTime' &&
      targetType !== 'answerAttachment'
    ) {
      console.error(
        `Attempted to set answer enablement of an unsupported type: "${targetType}"`,
      );
      return {};
    }

    let mappedValue = value;

    if (targetType === 'answerQuantity') {
      // strip units from enablement.  This would introduce unnecessary dependencies on unit changes
      mappedValue = { value: (value as fhir.Quantity).value };
    }

    return {
      [targetType]: mappedValue,
    };
  };

  private _customComparatorExtension(
    customComparator: '!=' | '<' | '<=' | '>' | '>=',
  ): Partial<fhir.QuestionnaireItemEnableWhen> {
    return {
      modifierExtension: [
        {
          url: surveyUrls.customComparator,
          valueString: customComparator,
        },
      ],
    };
  }

  get isValid(): boolean {
    if (!this._linkId || !this._comparator) return false;

    const answerRequired =
      this._comparator !== 'hasAnswer' &&
      this._comparator !== 'doesNotHaveAnswer';

    let hasAnswer = !isNil(this._targetValue);

    if (this._enablementDependentType === 'answerQuantity') {
      // quantity must have value property set
      hasAnswer = !isNil(this._targetValue) && !isNil(this._targetValue.value);
    }

    return !answerRequired || hasAnswer;
  }

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  get enablementRule(): fhir.QuestionnaireItemEnableWhen {
    const returnValue: fhir.QuestionnaireItemEnableWhen = {
      question: this._linkId,
    };

    if (this._comparator === 'hasAnswer') {
      return {
        ...returnValue,
        hasAnswer: true,
      };
    } else if (this._comparator === 'doesNotHaveAnswer') {
      return {
        ...returnValue,
        hasAnswer: false,
      };
    } else if (this._comparator === '=') {
      return {
        ...returnValue,
        ...this._answerEnablementValue(
          this._enablementDependentType,
          this._targetValue,
        ),
      };
    } else if (customEnablementComparators.includes(this._comparator)) {
      return {
        ...returnValue,
        ...this._answerEnablementValue(
          this._enablementDependentType,
          this._targetValue,
        ),
        ...this._customComparatorExtension(this._comparator),
      };
    } else {
      console.error(
        `Received a comparator of an unsupported type: ${this._comparator}`,
      );
    }
  }
}

/**
 * Helper class to update a questionnaire item
 */
export default class QuestionnaireItemUpdater {
  _item: fhir.QuestionnaireItem;

  constructor(existingQuestionnaireItem?: fhir.QuestionnaireItem) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._item = existingQuestionnaireItem || {
      linkId: null,
      type: null,
    };
  }

  _addModifierExtension(extension: fhir.Extension): QuestionnaireItemUpdater {
    if (!extension.url) return this;

    if (this._item.modifierExtension) {
      this._item.modifierExtension = [
        ...this._item.modifierExtension.filter(
          existingExtension => existingExtension.url !== extension.url,
        ),
        extension,
      ];
    } else {
      this._item.modifierExtension = [extension];
    }

    return this;
  }

  _clearModifierExtension(extensionUrl: string): QuestionnaireItemUpdater {
    if (!extensionUrl) return this;

    if (this._item.modifierExtension) {
      this._item.modifierExtension = this._item.modifierExtension.filter(
        existingExtension => existingExtension.url !== extensionUrl,
      );
    }

    return this;
  }

  /**
   * Clears all initial values on questionnaire item.  This is useful because only one initial
   * value property can be set per the fhir schema
   */
  _clearInitialValues() {
    [
      'initialBoolean',
      'initialDecimal',
      'initialInteger',
      'initialDate',
      'initialDateTime',
      'initialTime',
      'initialString',
      'initialUri',
      'initialAttachment',
      'initialCoding',
      'initialQuantity',
    ].forEach(initialValueProp => {
      if ((this._item as any)[initialValueProp] !== undefined) {
        (this._item as any)[initialValueProp] = undefined;
      }
    });
  }

  setOptionReference(optionReference: string): QuestionnaireItemUpdater {
    if (!optionReference) return this;

    if (!this._item.options) this._item.options = {};

    this._item.options.reference = optionReference;

    return this;
  }

  setEnableWhen(enableWhen: fhir.QuestionnaireItemEnableWhen[]) {
    if (!enableWhen) return this;

    this._item.enableWhen = enableWhen;

    return this;
  }

  setLinkId(linkId: string): QuestionnaireItemUpdater {
    this._item.linkId = linkId;
    return this;
  }

  setRequired(required: boolean): QuestionnaireItemUpdater {
    this._item.required = required;
    return this;
  }

  setRepeats(repeats: boolean): QuestionnaireItemUpdater {
    this._item.repeats = repeats;
    return this;
  }

  setType(
    type:
      | 'display'
      | 'group'
      | 'choice'
      | 'string'
      | 'text'
      | 'integer'
      | 'decimal'
      | 'boolean'
      | 'open-choice'
      | 'quantity'
      | 'date'
      | 'time'
      | 'dateTime'
      | 'attachment',
  ): QuestionnaireItemUpdater {
    this._item.type = type;
    return this;
  }

  setText(text: string): QuestionnaireItemUpdater {
    this._item.text = text;
    return this;
  }

  setInitialBoolean(value: boolean): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialBoolean = value;
    return this;
  }

  setInitialDecimal(value: number): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialDecimal = value;
    return this;
  }

  setInitialInteger(value: number): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialInteger = value;
    return this;
  }

  setInitialString(value: string): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialString = value;
    return this;
  }

  setInitialQuantity(value: fhir.Quantity): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialQuantity = value;
    return this;
  }

  setInitialCoding(value: fhir.Coding): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialCoding = value;
    return this;
  }

  setInitialTime(value: fhir.time): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialTime = value;
    return this;
  }

  setInitialDate(value: fhir.date): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialDate = value;
    return this;
  }

  setInitialDateTime(value: fhir.dateTime): QuestionnaireItemUpdater {
    this._clearInitialValues();
    this._item.initialDateTime = value;
    return this;
  }

  setMarkdownDisplay(): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.markdownDisplay,
    });
    return this;
  }

  setQuestionType(type: SurveyBuilderQuestionTypes): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.builderQuestionType,
      valueString: type,
    });
    return this;
  }

  setTypographyVariant(
    variant: SurveyItemTypographyVariants,
  ): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.displayTypographyVariant,
      valueString: variant,
    });
    return this;
  }

  setDenseMargins(): QuestionnaireItemUpdater {
    this._addModifierExtension({ url: surveyUrls.denseMargins });
    return this;
  }

  setSameLine(): QuestionnaireItemUpdater {
    this._addModifierExtension({ url: surveyUrls.sameLine });
    return this;
  }

  setIndentation(indentationCount: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.indentation,
      valueInteger: indentationCount,
    });
    return this;
  }

  setMinNumericBounds(minBounds: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.minNumericBounds,
      valueDecimal: minBounds,
    });
    return this;
  }

  setMaxNumericBounds(maxBounds: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.maxNumericBounds,
      valueDecimal: maxBounds,
    });
    return this;
  }

  setMinSelectionCount(minSelectionCount: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.minSelectionCount,
      valueInteger: Math.floor(minSelectionCount),
    });
    return this;
  }

  setMaxSelectionCount(maxSelectionCount: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.maxSelectionCount,
      valueInteger: Math.floor(maxSelectionCount),
    });
    return this;
  }

  setMinCharCount(minCount: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.minCharCount,
      valueInteger: minCount,
    });
    return this;
  }

  setMaxCharCount(maxCount: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.maxCharCount,
      valueInteger: maxCount,
    });
    return this;
  }

  setRadioButtonGroup(): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.radioButtonGroup,
    });
    return this;
  }

  setAsTableGroup(): QuestionnaireItemUpdater {
    this._item.type = 'group';
    this._addModifierExtension({ url: surveyUrls.tableGroup });
    return this;
  }

  setReferenceQuantity(quantity: fhir.Quantity): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.referenceQuantity,
      valueQuantity: quantity,
    });
    return this;
  }

  setValuePrecision(precision: number): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.valuePrecision,
      valueInteger: precision,
    });

    return this;
  }

  private filterItemCode(isUserDefined: boolean): fhir.Coding[] {
    return (this._item?.code || [])
      .filter(Boolean)
      .filter(_ => !!_.userSelected == isUserDefined);
  }

  setGeneratedCode(display?: string): QuestionnaireItemUpdater {
    const existingUserSelectedCode = this.filterItemCode(true);

    this._item.code = [
      ...existingUserSelectedCode,
      {
        system: surveyUrls.itemCode,
        code: uuidv4(),
        display:
          display ||
          [
            this._item.text,
            `(${SurveyItem.getHumanReadableLink(this._item.linkId)})`,
          ]
            .filter(Boolean)
            .join(' '),
      },
    ];
    return this;
  }

  setUserDefinedCode(newCoding: fhir.Coding): QuestionnaireItemUpdater {
    const withoutUserDefinedCoding = this.filterItemCode(false);
    this._item.code = [
      ...withoutUserDefinedCoding,
      {
        ...newCoding,
        userSelected: true,
      },
    ];

    return this;
  }

  setOption(
    options: any[] = [],
    optionType:
      | 'valueString'
      | 'valueInteger'
      | 'valueDate'
      | 'valueTime' = 'valueString',
  ): QuestionnaireItemUpdater {
    this._item.option = options.map(option => ({
      [optionType]: option,
    }));
    return this;
  }

  setSubItems(items: fhir.QuestionnaireItem[]): QuestionnaireItemUpdater {
    this._item.item = items;
    return this;
  }

  setCheckpointPage(): QuestionnaireItemUpdater {
    this._addModifierExtension({
      url: surveyUrls.checkpointPage,
    });
    return this;
  }

  setOrRules(orRules: boolean): QuestionnaireItemUpdater {
    // OR requires an extension, AND is the default
    if (orRules) {
      this._addModifierExtension({
        url: surveyUrls.orEnablementRules,
      });
    }

    return this;
  }

  clearOrRules(): QuestionnaireItemUpdater {
    return this._clearModifierExtension(surveyUrls.orEnablementRules);
  }

  clearEnablementRules(): QuestionnaireItemUpdater {
    this._item.enableWhen = [];
    return this;
  }

  setUpdatesForbidden(): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.updatesForbidden,
    });
  }

  setSumScoreSection(): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.sumScoreSection,
    });
  }

  setOmitScore(): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.omitScore,
    });
  }

  setAvgScoreSection(): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.avgScoreSection,
    });
  }

  setImageId(imageId: string): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.imageId,
      valueString: imageId,
    });
  }

  setUnitLabel(unitLabel: string): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.unitLabel,
      valueString: unitLabel,
    });
  }

  setLifeologyCourseUrl(url: string): QuestionnaireItemUpdater {
    if (url) {
      this._addModifierExtension({
        url: surveyUrls.lifeologyCourseUrl,
        valueUri: url,
      });
    }
    return this;
  }

  setAutopopulateResourceType(
    autopopulateResourceType: AutopopulateSourceResourceType,
  ): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.autopopulateValues.resourceType,
      valueString: autopopulateResourceType,
    });
  }

  setAutopopulateQueryParams(
    autopopulateQueryParams: string,
  ): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.autopopulateValues.queryParams,
      valueString: autopopulateQueryParams,
    });
  }

  setAutopopulateValueSelector(
    autopopulateValueSelector: string,
  ): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.autopopulateValues.valueSelector,
      valueString: autopopulateValueSelector,
    });
  }

  setForceMatrixTable(): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.forceMatrixTable,
    });
  }

  setConfidenceLevelValues(coding: fhir.Coding[]): QuestionnaireItemUpdater {
    return this._addModifierExtension({
      url: surveyUrls.evidenceLevelValues,
      valueCodeableConcept: {
        coding,
      },
    });
  }

  get questionnaireItem(): fhir.QuestionnaireItem {
    return this._item;
  }
}
